function _extends() { _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }

function _objectWithoutProperties(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }

function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }

import React, { useRef, useState, useEffect } from 'react';
import ReactModal from 'react-modal';
import styled, { css } from 'styled-components';
import Cancel from '../icons/solid/Cancel';
import { padding, themify, margin } from '../styles/mixins';
import { colors, type, z } from '../styles/theme';
import { Box } from './Grid';
import { Button } from './Button';
import { Book } from './helpers/FontWeight';
import { Icon } from './Icon';
ReactModal.setAppElement('body');
export function ReactModalAdapter(_ref) {
  let {
    className = '',
    modalClassName
  } = _ref,
      props = _objectWithoutProperties(_ref, ["className", "modalClassName"]);

  return /*#__PURE__*/React.createElement(ReactModal, _extends({
    className: modalClassName,
    portalClassName: className,
    bodyOpenClassName: "portalOpen"
  }, props));
}
export const StyledReactModal = styled(ReactModalAdapter).attrs({
  overlayClassName: 'Overlay',
  modalClassName: 'Modal'
}).withConfig({
  displayName: "Modal__StyledReactModal",
  componentId: "sc-1aijnu4-0"
})(["& .Overlay{position:fixed;top:0;right:0;bottom:0;left:0;z-index:", ";width:100%;height:100vh;opacity:0;overflow:hidden;background:", ";cursor:", ";transition:", ";transition-duration:", "ms;&[class*='--after-open']{opacity:1;}&[class*='--before-close']{opacity:0;}}& .Modal{position:relative;z-index:", ";width:calc(100% - 36px);background:", ";box-shadow:0 0 60px -24px ", ";overflow:hidden;opacity:", ";transform:", ";transition:", ";cursor:default;", ";&:focus{outline:0;}&[class*='--after-open']{opacity:1;transform:", ";}&[class*='--before-close']{opacity:", ";transform:", ";}}"], z.modalMask, x => `rgba(0, 0, 0, ${x.$mobile ? 0.5 : 0.75})`, x => x.shouldCloseOnOverlayClick ? 'pointer' : 'default', x => `opacity ${x.closeTimeoutMS}ms ${x.theme.curves.standard}`, x => x.closeTimeoutMS, z.modal, x => x.$bgColor, colors.grayBlack, x => x.$mobile ? 1 : 0, x => x.$mobile ? 'translate(100%, 0)' : 'translate(0, -20vh)', x => `all ${x.closeTimeoutMS}ms ${x.theme.curves.decelerate}`, x => !x.$mobile && css(["top:6vh;margin:0 auto;width:", ";max-height:100%;max-width:calc(100% - 24px);border-radius:3px;"], y => y.$width ? y.theme.containers[y.$width] : 'auto'), x => x.$mobile ? 'translate(36px, 0)' : 'translate(0)', x => x.$mobile ? 1 : 0, x => x.$mobile ? 'translate(100%, 0)' : 'translate(0, -20vh)');
const ModalHeader = styled.div.withConfig({
  displayName: "Modal__ModalHeader",
  componentId: "sc-1aijnu4-1"
})(["", ";", ";position:relative;display:flex;justify-content:space-between;align-items:center;color:", ";background:", ";font-size:", ";box-shadow:0 6px 9px -6px ", ";border-bottom:1px solid ", ";"], padding(2), margin(-1, 'x'), x => x.$headerColor, x => x.$headerBgColor, x => type.scale[x.$headerFontSize], colors.grayLight, colors.grayLight);
const ModalContent = styled.div.withConfig({
  displayName: "Modal__ModalContent",
  componentId: "sc-1aijnu4-2"
})(["", ";background:", ";overflow:auto;height:100%;", ";"], x => padding(x.$contentPadding), x => x.$bgColor, x => x.$mobile ? css(["height:", ";max-height:-webkit-fill-available;"], y => `calc(${typeof window !== 'undefined' ? window.innerHeight : 800}px - ${y.$modalHeaderHeight + y.$modalFooterHeight}px)`) : css(["min-height:", ";height:", ";max-height:", ";"], y => y.$minHeight, y => y.$height, y => `calc(88vh - ${y.$modalHeaderHeight + y.$modalFooterHeight}px)`));
const ModalFooter = styled.div.withConfig({
  displayName: "Modal__ModalFooter",
  componentId: "sc-1aijnu4-3"
})(["", ";position:sticky;width:100%;background:", ";text-align:right;border-top:1px solid ", ";box-shadow:0 -6px 9px -6px ", ";z-index:1;border-radius:", ";"], padding(1), x => x.$footerBgColor, colors.grayLight, colors.grayLight, x => !x.$mobile ? '0 0 3px 3px' : '0');
const ModalButton = styled(Button).withConfig({
  displayName: "Modal__ModalButton",
  componentId: "sc-1aijnu4-4"
})(["width:", ";vertical-align:middle;"], x => x.$mobile ? '100%' : 'auto');

function useForceUpdate() {
  const [, setValue] = useState(0); // integer state

  return () => setValue(value => ++value); // update the state to force render
}

export const Modal = (_ref2) => {
  let {
    bgColor = 'grayLighter',
    buttons,
    children,
    contentLabel,
    contentPadding = 2,
    footerBgColor = 'white',
    headerBgColor = 'white',
    headerColor = 'grayDarker',
    headerFontSize = 4,
    height = 'auto',
    isOpen,
    minHeight = 'auto',
    mobile = false,
    onRequestClose,
    shouldCloseOnOverlayClick = true,
    timeout = 250,
    title,
    size = 'md',
    width = 'md'
  } = _ref2,
      props = _objectWithoutProperties(_ref2, ["bgColor", "buttons", "children", "contentLabel", "contentPadding", "footerBgColor", "headerBgColor", "headerColor", "headerFontSize", "height", "isOpen", "minHeight", "mobile", "onRequestClose", "shouldCloseOnOverlayClick", "timeout", "title", "size", "width"]);

  const [headerHeight, setHeaderHeight] = useState(0);
  const [footerHeight, setFooterHeight] = useState(0);
  const headerRef = useRef(null);
  const footerRef = useRef(null);
  const forceUpdate = useForceUpdate();
  useEffect(() => {
    if (isOpen) {
      if ((title || contentLabel) && !headerHeight && !headerRef.current) forceUpdate();
      if (buttons && buttons.length > 0 && !footerHeight && !footerRef.current) forceUpdate();
      if (headerRef.current) setHeaderHeight(headerRef.current.offsetHeight);
      if (footerRef.current) setFooterHeight(footerRef.current.offsetHeight);
    }
  }, [buttons, contentLabel, title, headerHeight, footerHeight, forceUpdate, isOpen]);
  return /*#__PURE__*/React.createElement(StyledReactModal, _extends({
    $bgColor: themify(bgColor),
    $mobile: mobile,
    $width: size || width,
    closeTimeoutMS: timeout,
    contentLabel: title || contentLabel,
    isOpen: isOpen,
    onRequestClose: onRequestClose,
    role: "dialog",
    shouldCloseOnOverlayClick: shouldCloseOnOverlayClick
  }, props), (title || contentLabel) && /*#__PURE__*/React.createElement(ModalHeader, {
    $headerBgColor: themify(headerBgColor),
    $headerFontSize: headerFontSize,
    $headerColor: themify(headerColor),
    ref: headerRef
  }, /*#__PURE__*/React.createElement(Box, {
    px: 1
  }, /*#__PURE__*/React.createElement(Book, null, title || contentLabel)), onRequestClose && /*#__PURE__*/React.createElement(Box, {
    px: 1,
    style: {
      minWidth: 30
    }
  }, /*#__PURE__*/React.createElement(Icon, {
    fill: "gray",
    hoverColor: "red",
    icon: Cancel,
    onClick: onRequestClose,
    solid: true,
    width: "18px"
  }))), /*#__PURE__*/React.createElement(ModalContent, {
    $bgColor: themify(bgColor),
    $contentPadding: contentPadding,
    $height: height,
    $minHeight: minHeight,
    $mobile: mobile,
    $modalFooterHeight: footerHeight,
    $modalHeaderHeight: headerHeight
  }, children), buttons && buttons.length > 0 && /*#__PURE__*/React.createElement(ModalFooter, {
    $footerBgColor: themify(footerBgColor),
    $mobile: mobile,
    ref: footerRef
  }, buttons.map((buttonProps, index) => /*#__PURE__*/React.createElement(ModalButton, _extends({
    key: `button-${index}`,
    $mobile: mobile,
    mb: 0,
    mr: index === buttons.length - 1 ? 0 : 1,
    mt: mobile && index > 0 ? 1 : 0,
    fontSize: buttonProps.fontSize || 6
  }, buttonProps)))));
};